import React, { useMemo, useRef, useState } from "react";
import { FileText, Search, Upload, ExternalLink } from "lucide-react";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useAllRecruiters } from "@/hooks/queries/recruiters";
import { useUploadRecruiterPdf } from "@/hooks/mutations/recruiters";
import { useDebounce } from "@/utils/helpers";
import { getRecruiterPdfViewUrl } from "@/services/recruitersService";

const MAX_PDF_SIZE_BYTES = 5 * 1024 * 1024;

const isRecruiterRole = (recruiter) =>
  (recruiter?.roles ?? []).some(
    (role) => (role?.title ?? "").toLowerCase() === "recruiter",
  );

const hasRecruiterPdf = (recruiter) => Boolean(getRecruiterPdfViewUrl(recruiter));

const Recruiters = () => {
  const { showToast } = useToastStore;
  const { data: recruiters = [], isLoading, isError, refetch } = useAllRecruiters();
  const { mutate: uploadPdf, isPending: isUploading } = useUploadRecruiterPdf();
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [uploadingRecruiterId, setUploadingRecruiterId] = useState(null);
  const fileInputRefs = useRef({});

  const debouncedSearch = useDebounce((term) => setSearchTerm(term), 500);

  const handleSearchChange = (value) => {
    setTempSearchTerm(value);
    debouncedSearch(value);
  };

  const filteredRecruiters = useMemo(() => {
    const list = (Array.isArray(recruiters) ? recruiters : []).filter(
      isRecruiterRole,
    );
    if (!searchTerm.trim()) return list;

    const query = searchTerm.trim().toLowerCase();
    return list.filter(
      (recruiter) =>
        recruiter.name?.toLowerCase().includes(query) ||
        recruiter.email?.toLowerCase().includes(query),
    );
  }, [recruiters, searchTerm]);

  const handleFileSelect = (recruiterId, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      showToast({ message: "Please select a PDF file.", type: "error" });
      return;
    }

    if (file.size > MAX_PDF_SIZE_BYTES) {
      showToast({ message: "PDF must be 5MB or smaller.", type: "error" });
      return;
    }

    setUploadingRecruiterId(recruiterId);
    uploadPdf(
      { recruiterId, file },
      {
        onSuccess: () => {
          showToast({ message: "PDF uploaded successfully.", type: "success" });
          refetch();
        },
        onError: (error) => {
          showToast({
            message: error?.message || "Failed to upload PDF.",
            type: "error",
          });
        },
        onSettled: () => {
          setUploadingRecruiterId(null);
        },
      },
    );
  };

  const triggerFileInput = (recruiterId) => {
    fileInputRefs.current[recruiterId]?.click();
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading recruiters..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 px-8 mx-auto flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
              Recruiters
            </h1>
            <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
              View all recruiters and upload PDF documents for each recruiter.
            </p>
          </div>

          <div className="w-full md:w-[384px]">
            <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden w-full">
              <Search className="w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or email"
                value={tempSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load recruiters. Please try again.
          </div>
        )}

        <div className="overflow-x-auto rounded-lg shadow">
          <table className="min-w-full divide-y divide-border bg-card">
            <thead className="bg-muted border-b border-border">
              <tr className="text-left">
                <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  PDF
                </th>
                <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {filteredRecruiters.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-10 text-center text-sm text-muted-foreground"
                  >
                    No recruiters found.
                  </td>
                </tr>
              ) : (
                filteredRecruiters.map((recruiter) => {
                  const pdfViewUrl = getRecruiterPdfViewUrl(recruiter);
                  const isRowUploading =
                    isUploading && uploadingRecruiterId === recruiter.id;

                  return (
                    <tr
                      key={recruiter.id}
                      className="hover:bg-muted transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-md font-medium text-foreground">
                        {recruiter.name || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {recruiter.email || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {pdfViewUrl ? (
                          <a
                            href={pdfViewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-primary-dark hover:underline"
                          >
                            <FileText className="w-4 h-4" />
                            View PDF
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">No PDF uploaded</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          ref={(el) => {
                            fileInputRefs.current[recruiter.id] = el;
                          }}
                          type="file"
                          accept=".pdf,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileSelect(recruiter.id, e)}
                        />
                        <button
                          type="button"
                          onClick={() => triggerFileInput(recruiter.id)}
                          disabled={isRowUploading}
                          className="inline-flex items-center gap-2 rounded-lg border border-input bg-card px-3 py-2 text-sm font-semibold text-muted-foreground shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Upload className="w-4 h-4" />
                          {isRowUploading
                            ? "Uploading..."
                            : pdfViewUrl
                              ? "Replace PDF"
                              : "Add PDF"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Recruiters;
